import { z } from 'zod/v3';
import { TopicTypeSchema, WorkspaceTypeSchema } from './generated/enums'

/**
 * Section Template Types
 * These types define the structure for creating documentation sections from templates
 */

// Template categories
export type SectionTemplateCategory = 'product' | 'developer' | 'compliance' | 'custom'

// Template content types
export type SectionTemplateContentType = 'markdown' | 'import' | 'generated'

// Section template content
export interface SectionTemplateContent {
  type: SectionTemplateContentType
  source?: string
  template?: string
}

// Section template node (recursive structure)
export interface SectionTemplateNode {
  type: z.infer<typeof TopicTypeSchema>
  title: string
  description?: string
  content?: string | SectionTemplateContent
  children?: SectionTemplateNode[]
  config?: any
  placeholder?: boolean // Indicates this is a placeholder for future import
}

// Template input types
export interface SectionTemplateInput {
  id: string
  type: 'openapi' | 'changelog' | 'import' | 'text'
  label: string
  required: boolean
  placeholder?: string
  validation?: (value: any) => boolean | string
}

// Main section template interface
export interface SectionTemplate {
  id: string
  name: string
  description: string
  icon: string // Using string for icon name to avoid React dependency
  structure: SectionTemplateNode
  requiredInputs: SectionTemplateInput[]
  category: SectionTemplateCategory
  workspaceTypes?: z.infer<typeof WorkspaceTypeSchema>[] // Which workspace types this template is relevant for (undefined = all types)
}

// Zod schemas for validation
export const SectionTemplateContentSchema = z.object({
  type: z.enum(['markdown', 'import', 'generated']),
  source: z.string().optional(),
  template: z.string().optional(),
})

export const SectionTemplateNodeSchema: z.ZodType<SectionTemplateNode> = z.lazy(() =>
  z.object({
    type: TopicTypeSchema,
    title: z.string(),
    description: z.string().optional(),
    content: z.union([z.string(), SectionTemplateContentSchema]).optional(),
    children: z.array(SectionTemplateNodeSchema).optional(),
    config: z.any().optional(),
    placeholder: z.boolean().optional(),
  })
)

export const SectionTemplateInputSchema = z.object({
  id: z.string(),
  type: z.enum(['openapi', 'changelog', 'import', 'text']),
  label: z.string(),
  required: z.boolean(),
  placeholder: z.string().optional(),
  validation: z.function().optional(),
})

export const SectionTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string(),
  structure: SectionTemplateNodeSchema,
  requiredInputs: z.array(SectionTemplateInputSchema),
  category: z.enum(['product', 'developer', 'compliance', 'custom']),
  workspaceTypes: z.array(WorkspaceTypeSchema).optional(),
})

// Type exports
export type SectionTemplateNodeType = z.infer<typeof SectionTemplateNodeSchema>
export type SectionTemplateType = z.infer<typeof SectionTemplateSchema>